<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { api, useSession, errorText, dateTime } from "../api";
import "../styles/mcp.scss";
const route = useRoute(),
  session = useSession(),
  approval = ref<any>(),
  loading = ref(true),
  busy = ref(false),
  error = ref(""),
  notice = ref(""),
  name = ref("");
const now = ref(Date.now()),
  timer = setInterval(() => (now.value = Date.now()), 1000);
onUnmounted(() => clearInterval(timer));
const expired = computed(
  () =>
    approval.value && new Date(approval.value.expiresAt).getTime() <= now.value,
);
const actionable = computed(
  () =>
    approval.value &&
    !expired.value &&
    !approval.value.approved &&
    !approval.value.rejected &&
    !approval.value.consumed,
);
const changes = computed(() => {
  const details = approval.value?.details;
  if (!details?.proposed) return [];
  const old = new Map<string, any>(
    (details.current?.fields ?? []).map((f: any) => [f.id, f]),
  );
  const next = new Map<string, any>(
    details.proposed.fields.map((f: any) => [f.id, f]),
  );
  return [...new Set([...old.keys(), ...next.keys()])]
    .filter(
      (id) => JSON.stringify(old.get(id)) !== JSON.stringify(next.get(id)),
    )
    .map((id) => ({
      id,
      label: next.get(id)?.label ?? old.get(id)?.label,
      old: old.get(id),
      next: next.get(id),
    }));
});
const fieldSummary = (f: any) =>
  f
    ? `${f.label} · ${f.type}${f.unit ? " · " + f.unit : ""}${f.active === false ? " · deaktiveret" : ""}${f.nullable ? " · må være tomt" : " · påkrævet"}${f.queryable ? " · kan forespørges" : ""}`
    : "Findes ikke";
const countLabels: Record<string, string> = {
  records: "Rækker",
  sources: "Kilder",
  views: "Dashboardkort",
  automations: "Automatiseringer",
  report_definitions: "Rapporter",
  artifacts: "Filer",
};
let generation = 0;
async function load() {
  const version = ++generation;
  loading.value = true;
  error.value = "";
  approval.value = undefined;
  name.value = "";
  notice.value = "";
  try {
    const org = String(route.query.organization ?? "");
    if (!session.organizations.some((o) => o.id === org))
      throw new Error(
        "Vælg den konto, som anmodede om ændringen. Dette workspace er ikke tilgængeligt.",
      );
    if (session.organization !== org) session.organization = org;
    const result = await api(
      "/api/approvals/" + encodeURIComponent(String(route.params.id)),
    );
    if (version === generation) approval.value = result;
  } catch (e) {
    if (version === generation) error.value = errorText(e);
  } finally {
    if (version === generation) loading.value = false;
  }
}
watch(() => route.fullPath, load, { immediate: true });
async function decide(approve: boolean) {
  if (busy.value || !actionable.value) return;
  busy.value = true;
  error.value = "";
  try {
    const result = await api("/api/approvals/" + approval.value.id, {
      method: "POST",
      body: {
        approve,
        payloadHash: approval.value.payloadHash,
        ...(approve && approval.value.operation === "dataset.delete"
          ? { name: name.value }
          : {}),
      },
    });
    approval.value.expiresAt = result.expiresAt;
    approval.value.approved = approve;
    approval.value.rejected = !approve;
    notice.value = approve
      ? `Godkendt. Vend tilbage til din AI-klient, og bed den gennemføre handlingen før ${dateTime(result.expiresAt)}.`
      : "Afvist. Klienten kan ikke gennemføre denne ændring.";
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
</script>
<template>
  <section class="approval-page">
    <RouterLink to="/connections" class="text-button">Til AI & MCP</RouterLink>
    <h1>Gennemse ændringen</h1>
    <p v-if="loading" role="status">Henter den præcise ændring…</p>
    <p v-if="error" class="error" role="alert">
      {{ error }}
      <button class="text-button" :disabled="busy" @click="load">
        Hent igen
      </button>
    </p>
    <template v-if="approval">
      <p class="subtitle">
        <strong>{{ approval.connectionName }}</strong> vil
        {{
          approval.operation === "dataset.delete"
            ? "slette"
            : "ændre schemaet for"
        }}
        <strong>{{ approval.datasetName }}</strong
        >.
      </p>
      <dl class="approval-facts">
        <div>
          <dt>Workspace</dt>
          <dd>
            {{
              session.organizations.find((o) => o.id === session.organization)
                ?.name
            }}
          </dd>
        </div>
        <div>
          <dt>Udløber</dt>
          <dd>{{ dateTime(approval.expiresAt) }}</dd>
        </div>
      </dl>
      <template v-if="approval.operation === 'dataset.delete'">
        <h2>Permanent sletning</h2>
        <p>
          Adgangen lukkes straks, når klienten gennemfører handlingen. Data
          slettes ved næste natlige oprydning. Det kan ikke fortrydes.
        </p>
        <dl class="approval-facts approval-counts">
          <div v-for="(count, key) in approval.details?.counts" :key="key">
            <dt>{{ countLabels[String(key)] ?? key }}</dt>
            <dd>{{ count }}</dd>
          </div>
        </dl>
      </template>
      <template v-else>
        <h2>Feltændringer</h2>
        <p>
          {{ approval.details?.result?.total ?? 0 }} eksisterende rækker indgår
          i previewet. {{ approval.details?.result?.invalid ?? 0 }} rækker
          kræver rettelse.
        </p>
        <ul v-if="changes.length" class="field-changes">
          <li v-for="field in changes" :key="field.id">
            <h3>{{ field.label }}</h3>
            <dl>
              <div>
                <dt>Nuværende</dt>
                <dd>{{ fieldSummary(field.old) }}</dd>
              </div>
              <div>
                <dt>Foreslået</dt>
                <dd>{{ fieldSummary(field.next) }}</dd>
              </div>
            </dl>
          </li>
        </ul>
        <p v-else>Ingen ændringer i feltdefinitionerne.</p>
        <p v-if="approval.details?.result?.invalid" class="warning">
          Rækkerne skal rettes, før denne schemaændring kan godkendes. Bed
          klienten om et nyt preview bagefter.
        </p>
        <details>
          <summary>Se alle feltindstillinger før og efter</summary>
          <pre class="mcp-code">{{
            JSON.stringify(
              {
                før: approval.details?.current,
                efter: approval.details?.proposed,
              },
              null,
              2,
            )
          }}</pre>
        </details>
      </template>
      <p
        v-if="expired && !approval.consumed && !approval.rejected"
        class="warning"
        role="status"
      >
        Godkendelsen er udløbet. Bed din AI-klient om et nyt preview.
      </p>
      <p v-else-if="notice" class="success" role="status">{{ notice }}</p>
      <p v-else-if="approval.consumed" role="status">
        Godkendelsen er allerede brugt.
      </p>
      <p v-else-if="approval.rejected" role="status">
        Denne anmodning er afvist.
      </p>
      <p v-else-if="expired" class="warning" role="status">
        Godkendelsen er udløbet. Bed din AI-klient om et nyt preview.
      </p>
      <p v-else-if="approval.approved" role="status">
        Allerede godkendt. Vend tilbage til din AI-klient for at gennemføre
        handlingen.
      </p>
      <form
        v-if="actionable"
        class="form-stack approval-actions"
        @submit.prevent="decide(true)"
      >
        <label v-if="approval.operation === 'dataset.delete'"
          >Skriv “{{ approval.datasetName }}” for at godkende<input
            v-model="name"
            autocomplete="off"
            :disabled="busy"
            required
        /></label>
        <p class="muted">
          Godkendelsen gælder kun denne ændring og denne forbindelse. Den kan
          bruges én gang i højst fem minutter efter godkendelsen.
        </p>
        <div class="mcp-actions">
          <button
            :class="[
              'button',
              approval.operation === 'dataset.delete'
                ? 'destructive'
                : 'primary',
            ]"
            :disabled="
              busy ||
              (approval.operation === 'dataset.delete' &&
                name !== approval.datasetName) ||
              !!approval.details?.result?.invalid
            "
          >
            {{
              busy
                ? "Gemmer…"
                : approval.operation === "dataset.delete"
                  ? "Godkend permanent sletning"
                  : "Godkend schemaændring"
            }}</button
          ><button
            type="button"
            class="button secondary"
            :disabled="busy"
            @click="decide(false)"
          >
            Afvis ændringen
          </button>
        </div>
      </form>
    </template>
  </section>
</template>
<style scoped lang="scss" src="../styles/pages/Approval.scss"></style>
